// ==========================
// Version 3 - auth/VerifyEmail.js
// - Verification holding screen for email/password accounts
// - Allows resending verification email and refreshing Firebase user state
// - Waits for the initial auth check before redirecting
// ==========================
import React from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { sendEmailVerification } from 'firebase/auth';
import Seo from '../components/Seo';
import { auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { toast } from '../utils/toast';
import {
  getEmailVerificationActionSettings,
  needsEmailVerification,
} from './emailVerification';

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const IVORY = '#FDFCF9';
const WARM_GRAY = '#888888';
const BURGUNDY = '#5C1A2B';
const TEAL = '#0F766E';

function VerifyEmail() {
  const { currentUser, loading, logout, refreshCurrentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = React.useState(false);
  const [resent, setResent] = React.useState(!!location.state?.sent);
  const [error, setError] = React.useState('');

  const email = currentUser?.email || location.state?.email || '';
  const pageUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/verify-email`
    : undefined;

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>Checking sign-in…</div>
      </div>
    );
  }
  if (!currentUser) return <Navigate to="/login" replace />;
  if (!needsEmailVerification(currentUser)) return <Navigate to="/home" replace />;

  const handleResend = async () => {
    setError('');
    try {
      setBusy(true);
      await sendEmailVerification(auth.currentUser, getEmailVerificationActionSettings());
      setResent(true);
      toast.success('Verification email sent.');
    } catch (err) {
      setError(err?.message || 'Could not send verification email.');
    } finally {
      setBusy(false);
    }
  };

  const handleRefresh = async () => {
    setError('');
    try {
      setBusy(true);
      await refreshCurrentUser();
      if (auth.currentUser?.emailVerified) {
        toast.success('Email verified.');
        navigate('/home', { replace: true });
        return;
      }
      setError('Still waiting for verification. Please click the link in your email first.');
    } catch (err) {
      setError(err?.message || 'Could not refresh your account.');
    } finally {
      setBusy(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div style={styles.page}>
      <Seo
        title="Verify email • Blessed with a Kiss"
        description="Verify your email address to finish creating your BWK account."
        url={pageUrl}
        type="website"
      />

      <div style={styles.glowTL} />
      <div style={styles.glowBR} />

      <section style={styles.card} aria-label="Verify email">
        <h2 style={styles.title}>Verify your email</h2>
        <p style={styles.subtitle}>
          We sent a verification link to {email || 'your email address'}. Open it, then come back
          here to continue.
        </p>

        {resent ? (
          <div style={styles.success} role="status">
            Verification email sent. Check your inbox and spam folder.
          </div>
        ) : null}

        {error ? <div style={styles.error} role="alert">{error}</div> : null}

        <div style={styles.actions}>
          <button type="button" style={styles.primaryBtn} onClick={handleRefresh} disabled={busy}>
            {busy ? 'Checking...' : "I've verified"}
          </button>
          <button type="button" style={styles.secondaryBtn} onClick={handleResend} disabled={busy}>
            Resend email
          </button>
        </div>

        <div style={styles.footerRow}>
          <button type="button" style={styles.linkButton} onClick={handleLogout}>
            Use another account
          </button>
          <Link to="/" style={styles.link}>Back to home</Link>
        </div>
      </section>
    </div>
  );
}

const styles = {
  page: {
    minHeight: 'calc(100dvh - 64px)',
    background: NAVY,
    display: 'grid',
    placeItems: 'center',
    padding: 16,
    position: 'relative',
    overflow: 'hidden',
  },
  glowTL: {
    position: 'absolute',
    top: -120,
    left: -120,
    width: 280,
    height: 280,
    borderRadius: '50%',
    filter: 'blur(40px)',
    opacity: 0.25,
    background: GOLD,
  },
  glowBR: {
    position: 'absolute',
    bottom: -140,
    right: -140,
    width: 340,
    height: 340,
    borderRadius: '50%',
    filter: 'blur(50px)',
    opacity: 0.2,
    background: GOLD,
  },
  card: {
    width: 'min(92vw, 440px)',
    background: IVORY,
    borderRadius: 20,
    padding: 22,
    boxShadow: '0 24px 64px rgba(10,26,51,0.40)',
    border: `1px solid rgba(201,164,76,0.35)`,
    color: NAVY,
  },
  title: {
    margin: 0,
    fontFamily: 'serif',
    fontSize: 26,
  },
  subtitle: {
    margin: '8px 0 16px',
    color: WARM_GRAY,
    lineHeight: 1.5,
  },
  success: {
    margin: '8px 0 12px',
    background: 'rgba(15,118,110,0.10)',
    color: TEAL,
    border: '1px solid rgba(15,118,110,0.32)',
    padding: '8px 10px',
    borderRadius: 10,
    fontSize: 14,
  },
  error: {
    margin: '8px 0 12px',
    background: 'rgba(92,26,43,0.08)',
    color: BURGUNDY,
    border: '1px solid rgba(92,26,43,0.28)',
    padding: '8px 10px',
    borderRadius: 10,
    fontSize: 14,
  },
  actions: {
    display: 'grid',
    gap: 10,
  },
  primaryBtn: {
    padding: '12px 14px',
    borderRadius: 12,
    border: 'none',
    background: GOLD,
    color: NAVY,
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: 15,
  },
  secondaryBtn: {
    padding: '12px 14px',
    borderRadius: 12,
    border: `1px solid rgba(10,26,51,0.18)`,
    background: '#fff',
    color: NAVY,
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: 15,
  },
  footerRow: {
    marginTop: 16,
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  linkButton: {
    border: 'none',
    background: 'transparent',
    color: NAVY,
    padding: 0,
    cursor: 'pointer',
    fontWeight: 700,
    textDecoration: 'underline',
  },
  link: {
    color: NAVY,
    fontWeight: 700,
  },
};

export default VerifyEmail;

// ==========================
// End Version 2 - auth/VerifyEmail.js
// ==========================
